import {
  faPenToSquare,
  faThumbtack,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Anchor,
  Badge,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { ActionIconLink } from "@sweco-se/reality-frontend";
import type { AnchorHTMLAttributes, FC } from "react";
import Markdown from "react-markdown";

import type { ActiveNewsPost } from "@/hooks/useActiveNews.ts";

export const levelColors: Record<ActiveNewsPost["level"], string> = {
  info: "blue",
  warning: "alert",
};

const markdownComponents = {
  a: ({
    href,
    children,
    ...props
  }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <Anchor href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </Anchor>
  ),
};

export const NewsPostModal: FC<{
  post: ActiveNewsPost | null;
  isAdmin: boolean;
  onClose: () => void;
  onDismiss: (id: number) => void;
}> = ({ post, isAdmin, onClose, onDismiss }) => {
  const { t } = useLingui();

  return (
    <Modal
      opened={post !== null}
      onClose={onClose}
      title={
        post && (
          <Group gap="xs">
            <Badge color={levelColors[post.level]} variant="light">
              {post.level === "warning" ? (
                <Trans context="news level">Warning</Trans>
              ) : (
                <Trans context="news level">Info</Trans>
              )}
            </Badge>
            {post.pinned && <FontAwesomeIcon icon={faThumbtack} />}
            <Title order={4}>{post.title}</Title>
          </Group>
        )
      }
      size="lg"
    >
      {post && (
        <Stack gap="md">
          <ScrollArea.Autosize mah="60vh">
            <Markdown components={markdownComponents}>{post.body}</Markdown>
          </ScrollArea.Autosize>
          <Group justify="flex-end">
            {isAdmin && (
              <Tooltip label={t`Edit`}>
                <ActionIconLink
                  to="/admin/news/$id"
                  params={{ id: String(post.id) }}
                  variant="subtle"
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </ActionIconLink>
              </Tooltip>
            )}
            {!post.pinned && (
              <Tooltip label={t`Dismiss`}>
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    onDismiss(post.id);
                    onClose();
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export const NewsPostRow: FC<{
  post: ActiveNewsPost;
  isAdmin: boolean;
  onOpen: (post: ActiveNewsPost) => void;
  onDismiss: (id: number) => void;
}> = ({ post, isAdmin, onOpen, onDismiss }) => {
  const { t } = useLingui();

  return (
    <Group
      justify="space-between"
      px="md"
      py={6}
      bg={`var(--mantine-color-${levelColors[post.level]}-1)`}
      style={{ cursor: "pointer" }}
      onClick={() => onOpen(post)}
      wrap="nowrap"
    >
      <Group gap="xs" wrap="nowrap">
        {post.pinned && <FontAwesomeIcon icon={faThumbtack} />}
        <Text fw={500} size="sm" lineClamp={1}>
          {post.title}
        </Text>
      </Group>
      <Group gap="xs" wrap="nowrap">
        {isAdmin && (
          <Tooltip label={t`Edit`}>
            <span onClick={(e) => e.stopPropagation()}>
              <ActionIconLink
                to="/admin/news/$id"
                params={{ id: String(post.id) }}
                variant="subtle"
                size="sm"
              >
                <FontAwesomeIcon icon={faPenToSquare} />
              </ActionIconLink>
            </span>
          </Tooltip>
        )}
        {!post.pinned && (
          <Tooltip label={t`Dismiss`}>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(post.id);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Group>
  );
};
